import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import './index.css'
import App from './App.jsx'
import { assertMicrosoftAuthSupported, createMsalConfig } from './auth.js'
import { isFlagEnabled, loadRuntimeConfig } from './config.js'

const root = createRoot(document.getElementById('root'))

export function BootstrapError({ error }) {
  const isInsecureContextError = error.message.includes('requires HTTPS or localhost')

  return (
    <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>
      <h1>Frontend configuration error</h1>
      <p>{error.message}</p>
      {isInsecureContextError ? (
        <p>
          The Microsoft client ID and tenant ID can still be correct. For non-local environments,
          this frontend must be served over HTTPS before MSAL sign-in can work.
        </p>
      ) : (
        <p>Use the supported environment command to regenerate runtime config: `pixi run -e local up` for local or `pixi run -e cloud switch &lt;env&gt;` followed by `pixi run -e cloud deploy` for cloud.</p>
      )}
    </div>
  )
}

async function bootstrap() {
  try {
    const config = await loadRuntimeConfig()

    // Applied before the first render so the backdrop is present on the login
    // screen too, not just after auth. App.css keys off this attribute.
    //
    // On by default: the backdrop is the app's normal appearance, not an
    // experiment, so it needs no env var to show up. FRONTEND_BACKGROUND_IMAGE
    // survives as an opt-out — set it false/0/no/off to get the plain gradient.
    if (isFlagEnabled(config.backgroundImage, true)) {
      document.body.dataset.bgImage = 'on'
    }

    if (config.authMode === 'placeholder') {
      root.render(
        <StrictMode>
          <App config={config} />
        </StrictMode>,
      )
      return
    }

    assertMicrosoftAuthSupported()
    const msalInstance = new PublicClientApplication(createMsalConfig(config))
    await msalInstance.initialize()

    const existingAccounts = msalInstance.getAllAccounts()
    if (existingAccounts.length > 0) {
      msalInstance.setActiveAccount(existingAccounts[0])
    }

    root.render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <App config={config} />
        </MsalProvider>
      </StrictMode>,
    )
  } catch (error) {
    root.render(
      <StrictMode>
        <BootstrapError error={error} />
      </StrictMode>,
    )
  }
}

bootstrap()
